import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import Spinner from 'react-bootstrap/Spinner'
import { useParams } from 'react-router-dom'
import { getPublicGroupView } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCurrencies } from '../../hooks/useCurrencies'
import { formatDateTime } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'

/**
 * Veřejný náhled skupiny - dostupný bez přihlášení přes sdílený odkaz.
 * Vše je jen ke čtení; fotografie účtenek se sem záměrně nedostanou.
 */
export function PublicGroupPage() {
    const { token } = useParams<{ token: string }>()
    const [minimize, setMinimize] = useState(false)

    const { data: currencies } = useCurrencies()
    const { data, isLoading, isError } = useQuery({
        queryKey: ['public', token],
        queryFn: () => actionApiCall(() => getPublicGroupView({ path: { token: token ?? '' } }), { silent: true }),
        enabled: !!token,
        retry: false,
    })

    if (isLoading) {
        return <Spinner animation="border" />
    }

    if (isError || !data) {
        return (
            <Alert variant="warning">
                <Alert.Heading className="h5">Odkaz neplatí</Alert.Heading>
                <p className="mb-0">Odkaz mohl být zrušen nebo mu vypršela platnost. Požádejte o nový.</p>
            </Alert>
        )
    }

    const exponent = exponentOf(currencies, data.baseCurrencyCode)
    const memberName = (memberId: string) => data.members.find((member) => member.id === memberId)?.displayName ?? 'Neznámý'
    const settlements = minimize ? data.minimized : data.pairwise

    return (
        <>
            <div className="mb-3">
                <h1 className="h4 mb-1">{data.groupName}</h1>
                <div className="small text-secondary">
                    Veřejný náhled — pouze ke čtení · hlavní měna {data.baseCurrencyCode}
                </div>
            </div>

            <Card className="mb-3">
                <Card.Header>Bilance členů</Card.Header>
                <ListGroup variant="flush">
                    {data.balances.map((entry) => (
                        <ListGroup.Item key={entry.memberId} className="d-flex align-items-center gap-2">
                            <span className="flex-grow-1 text-truncate">{memberName(entry.memberId)}</span>
                            <span
                                className={`fw-semibold ${entry.balance === 0 ? 'text-secondary' : entry.balance > 0 ? 'text-success' : 'text-danger'}`}
                            >
                                {formatMoney(entry.balance, data.baseCurrencyCode, exponent)}
                            </span>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Card>

            <Card className="mb-3">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                    <span>Nevyrovnané dluhy</span>
                    <Form.Check
                        type="switch"
                        id="verejna-minimalizace"
                        label="Minimalizovat převody"
                        checked={minimize}
                        onChange={(event) => setMinimize(event.target.checked)}
                    />
                </Card.Header>
                <ListGroup variant="flush">
                    {settlements.map((settlement) => (
                        <ListGroup.Item key={`${settlement.fromMemberId}-${settlement.toMemberId}`} className="d-flex align-items-center gap-2">
                            <span className="flex-grow-1">
                                <span className="fw-semibold">{memberName(settlement.fromMemberId)}</span>
                                <i className="bi bi-arrow-right mx-2 text-secondary" aria-hidden="true" />
                                <span className="fw-semibold">{memberName(settlement.toMemberId)}</span>
                            </span>
                            <span className="fw-semibold">{formatMoney(settlement.amount, data.baseCurrencyCode, exponent)}</span>
                        </ListGroup.Item>
                    ))}
                    {settlements.length === 0 && <ListGroup.Item className="text-secondary">Všechno je vyrovnané.</ListGroup.Item>}
                </ListGroup>
            </Card>

            <Card>
                <Card.Header>Historie pohybů</Card.Header>
                <ListGroup variant="flush">
                    {data.transactions.map((transaction) => {
                        const mena = exponentOf(currencies, transaction.currencyCode)
                        return (
                            <ListGroup.Item key={transaction.id} className="d-flex align-items-start gap-2">
                                <div className="flex-grow-1 min-w-0">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-semibold text-truncate">{transaction.title}</span>
                                        {transaction.type === 'TRANSFER' && (
                                            <Badge bg="info" text="dark">
                                                Úhrada
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="small text-secondary">
                                        {transaction.type === 'TRANSFER'
                                            ? `${memberName(transaction.payers[0]?.memberId ?? '')} → ${memberName(transaction.shares[0]?.memberId ?? '')}`
                                            : `Zaplatil ${memberName(transaction.payers[0]?.memberId ?? '')}`}
                                    </div>
                                    <div className="small text-secondary">{formatDateTime(transaction.occurredAt)}</div>
                                </div>
                                <div className="text-end flex-shrink-0">
                                    <div className="fw-semibold">{formatMoney(transaction.amountTotal, transaction.currencyCode, mena)}</div>
                                    {transaction.currencyCode !== data.baseCurrencyCode && (
                                        <div className="small text-secondary">
                                            {formatMoney(transaction.amountTotalBase, data.baseCurrencyCode, exponent)}
                                        </div>
                                    )}
                                </div>
                            </ListGroup.Item>
                        )
                    })}
                    {data.transactions.length === 0 && <ListGroup.Item className="text-secondary">Zatím tu není žádný pohyb.</ListGroup.Item>}
                </ListGroup>
            </Card>

            <div className="small text-secondary mt-3">Data k {formatDateTime(data.generatedAt)}</div>
        </>
    )
}
